import type { Employee } from "types/employee";
import type {
  WorkOrder,
  WorkOrderAssetSummary,
  WorkOrderEmployeeSummary,
  WorkOrderLocationSummary,
  WorkOrderTask,
} from "types/workOrder";

export function formatWorkOrderAsset(a?: WorkOrderAssetSummary | null): string {
  if (!a) return "—";
  const tag = a.assetTag ?? a.asset_tag;
  const sn = a.serialNumber ?? a.serial_number;
  const parts = [tag, sn].filter(Boolean);
  if (parts.length) return parts.join(" · ");
  return a.model || "—";
}

export function formatWorkOrderEmployee(e?: WorkOrderEmployeeSummary | null): string {
  if (!e) return "—";
  const fn = e.firstName ?? e.first_name;
  const ln = e.lastName ?? e.last_name;
  const name = [fn, ln].filter(Boolean).join(" ");
  return name || e.email || "—";
}

export function formatWorkOrderLocation(l?: WorkOrderLocationSummary | null): string {
  if (!l) return "—";
  return l.name || l.address || l.city || "—";
}

export function workOrderTaskActionLabel(t: WorkOrderTask): string {
  const label = (t.actionLabel ?? t.action_label ?? t.action ?? "").toString().trim();
  return label || "—";
}

/** Ship-to for a task row (override location, else employee office / home). */
export function formatWorkOrderTaskShipTo(t: WorkOrderTask): string {
  if (t.location) return formatWorkOrderLocation(t.location);
  const e = t.employee;
  if (!e) return "—";
  const office = e.officeLocation ?? e.office_location;
  if (office) return formatWorkOrderLocation(office);
  const line1 = [e.homeAddress ?? e.home_address, e.homeAddress2 ?? e.home_address2].filter(Boolean).join(", ");
  const cityLine = [e.homeCity ?? e.home_city, e.homeState ?? e.home_state, e.homeZipcode ?? e.home_zipcode]
    .filter(Boolean)
    .join(" ");
  const country = e.homeCountry ?? e.home_country;
  const parts = [line1, cityLine, country].filter((s) => s && String(s).trim());
  return parts.length ? parts.join(", ") : "—";
}

/** One-line summary for employee list / chips. */
export function formatEmployeeHomeOneLine(
  emp: Pick<Employee, "homeAddress" | "homeAddress2" | "homeCity" | "homeZipcode" | "homeCountry">
): string {
  const line1 = [emp.homeAddress, emp.homeAddress2].filter(Boolean).join(", ");
  const cityLine = [emp.homeCity, emp.homeZipcode, emp.homeCountry].filter(Boolean).join(" ");
  const parts = [line1, cityLine].filter(Boolean);
  return parts.join(" · ") || "—";
}

export function employeeHasShipTo(emp: Employee | null | undefined): boolean {
  if (!emp) return false;
  const office = emp.officeLocationId ?? emp.officeLocation?.id;
  const home =
    emp.homeAddress?.trim() ||
    emp.homeCity?.trim() ||
    emp.homeZipcode?.trim() ||
    emp.homeCountry?.trim();
  return Boolean(office || home);
}

/** Ship-to label for a work order line when not using an override delivery location. */
export function formatEmployeeShipToDisplay(emp: Employee | null): string {
  if (!emp) return "—";
  const homeParts = [
    [emp.homeAddress, emp.homeAddress2].filter(Boolean).join(", "),
    [emp.homeCity, emp.homeState, emp.homeZipcode].filter(Boolean).join(", "),
    emp.homeCountry,
  ].filter((s) => s && String(s).trim());
  if (homeParts.length) return homeParts.join("\n");
  if (emp.officeLocation) {
    const o = emp.officeLocation;
    const summary: WorkOrderLocationSummary = {
      id: Number(o.id),
      uuid: o.uuid,
      name: o.name,
      address: o.address,
      address2: o.address2,
      city: o.city,
      state: o.state,
      zipcode: o.zipcode,
      country: o.country,
    };
    return formatWorkOrderLocation(summary);
  }
  return "—";
}

export function idsFromWorkOrder(wo: Partial<WorkOrder>) {
  return {
    assetId:
      wo.asset?.id != null
        ? String(wo.asset.id)
        : wo.assetId != null
          ? String(wo.assetId)
          : "",
    employeeId:
      wo.employee?.id != null
        ? String(wo.employee.id)
        : wo.employeeId != null
          ? String(wo.employeeId)
          : "",
    locationId:
      wo.location?.id != null
        ? String(wo.location.id)
        : wo.locationId != null
          ? String(wo.locationId)
          : "",
  };
}
